import { type FC, type MutableRefObject, useCallback, useState, useEffect, useMemo } from 'react';

import { CarouselControl } from '@vakantiesnl/components/src/atoms/CarouselControl/CarouselControl';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import ImageComponent from '@vakantiesnl/image-component';
import { type Nbc, type Search } from '@vakantiesnl/types';
import Carousel, { type ControlProps } from 'nuka-carousel';

import useCustomStyles from './ImageSlider.style';

type ImageSliderProps = {
	images: Nbc.MappedBucketImgProps[] | Search.TopImageItem[];
	alt: string;
	currentImageIndex?: number;
	containerClassName?: string;
	imageClassName?: string;
	imageSizes?: string;
	onChange?: (id: number) => void;
	hasCenterControls?: boolean;
	carouselRef?: MutableRefObject<HTMLDivElement | null>;
	trackAccoSliderClick?: (currentSlide: number, direction: 'left' | 'right') => void;
	loader?: false | 'CloudImage' | 'Contentful';
};

export const ImageSlider: FC<ImageSliderProps> = ({
	images,
	alt,
	currentImageIndex = 0,
	containerClassName,
	imageClassName,
	onChange,
	carouselRef,
	imageSizes,
	trackAccoSliderClick,
	loader,
}) => {
	const { isDesktop } = useBreakpoints();
	const defaultImageSizes = useMemo(() => (isDesktop ? '20vw' : '50vw'), [isDesktop]);
	const [currentIndex, setCurrentIndex] = useState<number>(currentImageIndex);
	const { classes, cx } = useCustomStyles();

	useEffect(() => {
		onChange && onChange(currentIndex);
	}, [onChange, currentIndex]);

	const slideHandler = useCallback(
		(startingIndex: number, currentIndex: number) => {
			trackAccoSliderClick && trackAccoSliderClick(currentIndex, currentIndex < startingIndex ? 'left' : 'right');
		},
		[trackAccoSliderClick],
	);

	const renderLeftButton = useCallback(
		({ previousSlide }: ControlProps) => <CarouselControl size="small" onClick={previousSlide} icon="arrowLeft" />,
		[],
	);

	const renderRightButton = useCallback(
		({ nextSlide }: ControlProps) => <CarouselControl size="small" onClick={nextSlide} icon="arrowRight" />,
		[],
	);

	return (
		<div className={cx(containerClassName)}>
			<Carousel
				slideIndex={currentIndex}
				renderCenterLeftControls={currentIndex > 0 ? renderLeftButton : null}
				renderCenterRightControls={currentIndex < images.length - 1 ? renderRightButton : null}
				renderBottomCenterControls={null}
				afterSlide={setCurrentIndex}
				beforeSlide={slideHandler}
				ref={carouselRef}
				dragging={!isDesktop}
				dragThreshold={0.2}
				adaptiveHeight={false}
				slidesToShow={1}
				disableAnimation
				disableEdgeSwiping
				enableKeyboardControls
			>
				{images.map((image, index) => {
					return (
						<div className={cx(classes.imageWrapper, imageClassName)} key={image.id ?? `image${index}`}>
							<ImageComponent
								alt={`${alt}-${index + 1}`}
								className={classes.image}
								fill={true}
								src={image.url}
								sizes={imageSizes ?? defaultImageSizes}
								useLoader={loader}
							/>
						</div>
					);
				})}
			</Carousel>
		</div>
	);
};
